import { useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { routePaths } from "../constants/routes";
import { topicNavigation } from "../config/siteNavigation";
import "../style/articleSidebar.css";

const actionLinks = [
  { to: routePaths.automationTesting, label: "Practice Lab", note: "Try live UI automation tasks" },
  { to: routePaths.enroll, label: "Join Course", note: "Live training and job support" },
  { to: routePaths.contact, label: "Ask Mentor", note: "Get guidance before choosing" },
];

const toolLinks = [
  { href: "https://www.selenium.dev/", label: "Selenium", note: "Browser automation" },
  { href: "https://playwright.dev/", label: "Playwright", note: "Modern end-to-end testing" },
  { href: "https://www.postman.com/", label: "Postman", note: "API testing workspace" },
];

function getInitialOpenGroups(pathname) {
  return topicNavigation.reduce((groups, section) => {
    const hasActiveItem = section.items.some((item) => item.to === pathname);
    if (hasActiveItem || groups.length < 2) {
      groups.push(section.group);
    }
    return groups;
  }, []);
}

export default function ArticleSidebar({
  as: Tag = "aside",
  className = "scrolling-sidebar article-sidebar",
}) {
  const { pathname } = useLocation();
  const [query, setQuery] = useState("");
  const [openGroups, setOpenGroups] = useState(() => getInitialOpenGroups(pathname));

  const filteredNavigation = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) {
      return topicNavigation;
    }

    return topicNavigation
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => (
          item.label.toLowerCase().includes(normalizedQuery) ||
          section.group.toLowerCase().includes(normalizedQuery)
        )),
      }))
      .filter((section) => section.items.length > 0);
  }, [query]);

  const toggleGroup = (group) => {
    setOpenGroups((currentGroups) => (
      currentGroups.includes(group)
        ? currentGroups.filter((item) => item !== group)
        : [...currentGroups, group]
    ));
  };

  return (
    <Tag className={className}>
      <div className="article-sidebar-shell">
        <div className="article-sidebar-intro">
          <span className="article-sidebar-eyebrow">QA Learning Map</span>
          <h2>All Testing Topics</h2>
          <p>
            Jump to any guide without hitting broken routes. These links are synced with the app routes.
          </p>
        </div>

        <label className="article-sidebar-search">
          <span>Search topics</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Manual, API, SDLC..."
            aria-label="Search article topics"
          />
        </label>

        <div className="article-sidebar-actions" aria-label="Recommended actions">
          {actionLinks.map((link) => (
            <NavLink to={link.to} className="article-sidebar-action" key={link.to}>
              <strong>{link.label}</strong>
              <span>{link.note}</span>
            </NavLink>
          ))}
        </div>

        <nav className="article-sidebar-nav" aria-label="Article topic navigation">
          {filteredNavigation.length > 0 ? (
            filteredNavigation.map((section) => {
              const isOpen = query || openGroups.includes(section.group);

              return (
                <section className="article-sidebar-group" key={section.group}>
                  <button
                    type="button"
                    className="article-sidebar-group-toggle"
                    onClick={() => toggleGroup(section.group)}
                    aria-expanded={Boolean(isOpen)}
                  >
                    <span>{section.group}</span>
                    <small>{section.items.length}</small>
                  </button>

                  {isOpen && (
                    <ul className="article-sidebar-list">
                      {section.items.map((link) => (
                        <li key={link.to}>
                          <NavLink
                            to={link.to}
                            className={({ isActive }) => (
                              isActive ? "article-sidebar-link active" : "article-sidebar-link"
                            )}
                          >
                            {link.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              );
            })
          ) : (
            <div className="article-sidebar-empty">
              <strong>No topic found</strong>
              <p>Try searching for manual, automation, API, testing types, or interview.</p>
            </div>
          )}
        </nav>

        <div className="article-sidebar-card">
          <h3>Popular Tools</h3>
          {toolLinks.map((link) => (
            <a href={link.href} key={link.href} target="_blank" rel="noreferrer noopener">
              <strong>{link.label}</strong>
              <span>{link.note}</span>
            </a>
          ))}
        </div>
      </div>
    </Tag>
  );
}
